import React, { useEffect, useState } from 'react';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

const ACCENTS = {
  todo: '#ffd600',
  inprogress: '#ff8c42',
  done: '#f76ea8',
};

const STATUSES = [
  { id: 'todo', label: 'To Do', color: ACCENTS.todo },
  { id: 'inprogress', label: 'In Progress', color: ACCENTS.inprogress },
  { id: 'done', label: 'Done', color: ACCENTS.done },
];

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [newContent, setNewContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mutateError, setMutateError] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'tasks'),
      (snapshot) => {
        setTasks(snapshot.docs.map(d => ({ ...d.data(), id: d.id })));
        setLoading(false);
      },
      (err) => {
        setError('Firestore error: ' + err.message);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  const addTask = async () => {
    if (!newContent.trim()) return;
    try {
      await addDoc(collection(db, 'tasks'), {
        content: newContent.trim(),
        status: 'todo',
        created: serverTimestamp(),
      });
      setNewContent('');
      setMutateError(null);
    } catch (e) {
      setMutateError('Failed to add task: ' + e.message);
    }
  };

  const deleteTask = async (id) => {
    try {
      await deleteDoc(doc(db, 'tasks', id));
      setMutateError(null);
    } catch (e) {
      setMutateError('Failed to delete task: ' + e.message);
    }
  };

  const onDragStart = (e, id) => e.dataTransfer.setData('id', id);
  const onDrop = async (e, status) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('id');
    try {
      await updateDoc(doc(db, 'tasks', id), { status });
      setMutateError(null);
    } catch (e) {
      setMutateError('Failed to update task: ' + e.message);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: '#1e1e2e' }}>
          Tasks Board
        </h1>
        <p style={{ margin: '6px 0 0 0', color: '#7f849c', fontSize: 15 }}>
          Drag cards between columns to update status. Changes sync in real time via Firestore.
        </p>
      </div>

      {/* Add task row */}
      <div style={{
        display: 'flex',
        gap: 12,
        marginBottom: 32,
        flexWrap: 'wrap',
      }}>
        <input
          style={{
            flex: '1 1 260px',
            maxWidth: 400,
            padding: '12px 18px',
            fontSize: 15,
            border: '2px solid #ff8c42',
            borderRadius: 12,
            outline: 'none',
            background: '#fff',
            color: '#1e1e2e',
            boxShadow: '0 2px 8px #ffeaa844',
          }}
          placeholder="Add a new task…"
          value={newContent}
          onChange={e => setNewContent(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addTask()}
        />
        <button
          onClick={addTask}
          style={{
            padding: '12px 28px',
            fontWeight: 700,
            fontSize: 15,
            borderRadius: 12,
            border: 'none',
            background: '#ff8c42',
            color: '#fff',
            cursor: 'pointer',
            boxShadow: '0 2px 12px #ff8c4244',
          }}
        >
          + Add Task
        </button>
      </div>

      {mutateError && (
        <div style={{ color: '#e53e3e', marginBottom: 16, fontSize: 14, fontWeight: 500 }}>
          {mutateError}
        </div>
      )}

      {/* Kanban board */}
      {error ? (
        <div style={{ color: 'red', fontWeight: 600 }}>{error}</div>
      ) : (
        <div style={{
          display: 'flex',
          gap: 24,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}>
          {STATUSES.map(col => (
            <div
              key={col.id}
              onDrop={e => onDrop(e, col.id)}
              onDragOver={e => e.preventDefault()}
              style={{
                flex: '1 1 240px',
                minWidth: 240,
                maxWidth: 360,
                background: '#fff',
                borderRadius: 20,
                padding: '20px 16px',
                boxShadow: '0 2px 16px #0000000d',
                borderTop: `4px solid ${col.color}`,
              }}
            >
              <h2 style={{
                margin: '0 0 16px 0',
                fontSize: 16,
                fontWeight: 700,
                color: col.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                {col.label}
                <span style={{
                  background: col.color + '22',
                  color: col.color,
                  borderRadius: 20,
                  padding: '2px 10px',
                  fontSize: 13,
                  fontWeight: 700,
                }}>
                  {loading ? '—' : tasks.filter(t => t.status === col.id).length}
                </span>
              </h2>

              {loading ? (
                <p style={{ color: '#7f849c', fontSize: 13 }}>Loading…</p>
              ) : (
                tasks
                  .filter(t => t.status === col.id)
                  .map(task => (
                    <div
                      key={task.id}
                      draggable
                      onDragStart={e => onDragStart(e, task.id)}
                      style={{
                        background: `linear-gradient(135deg, #fff, ${col.color}11)`,
                        borderLeft: `5px solid ${col.color}`,
                        borderRadius: 14,
                        padding: '14px 12px 14px 16px',
                        marginBottom: 12,
                        boxShadow: `0 2px 10px ${col.color}33`,
                        cursor: 'grab',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        fontSize: 14,
                        fontWeight: 500,
                        color: '#1e1e2e',
                      }}
                    >
                      <span style={{ flex: 1, lineHeight: 1.5 }}>{task.content}</span>
                      <button
                        onClick={() => deleteTask(task.id)}
                        title="Delete task"
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#f76ea8',
                          fontSize: 18,
                          cursor: 'pointer',
                          borderRadius: 8,
                          padding: '2px 7px',
                          lineHeight: 1,
                          flexShrink: 0,
                        }}
                        onMouseOver={e => (e.currentTarget.style.background = '#ffe8f3')}
                        onMouseOut={e => (e.currentTarget.style.background = 'none')}
                      >
                        ✕
                      </button>
                    </div>
                  ))
              )}

              {!loading && tasks.filter(t => t.status === col.id).length === 0 && (
                <div style={{
                  textAlign: 'center',
                  color: '#bbb',
                  fontSize: 13,
                  padding: '20px 0',
                  border: `2px dashed ${col.color}55`,
                  borderRadius: 12,
                }}>
                  Drop tasks here
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
